<template>
    <div class="message waba-template-message">
        <div class="content">
            <div class="meta-header" v-if="getMetaText()">
                <i class="material-icons">info</i>
                {{ getMetaText() }}
            </div>
            <div class="header-text" v-if="meta.headerText">{{ meta.headerText }}</div>

            <div class="body">{{ meta.body }}</div>
            <div class="buttons" v-if="meta.buttons.length > 0">
                <div
                    class="single-button"
                    v-for="(button, i) in meta.buttons"
                    :class="{ selected: false }"
                    :key="button.id"
                    @click=""
                >
                    <div class="single-button-text" v-html="button.text" />
                </div>
            </div>
            <div class="footer" v-if="meta.footer">{{ meta.footer }}</div>
        </div>
        <div class="carousel-messages" v-if="meta.templateType === 'CAROUSEL'">
            <div class="carousel-messages-item" v-for="card of meta.cards">
                <template v-if="!card.mediaUrl">
                    <img width="100%" :src="getDefaultImage(card.headerType)" alt="''" />
                </template>
                <template v-else>
                    <div v-if="card.headerType === 'VIDEO'">
                        <video width="100%" :src="card.mediaUrl" controls />
                    </div>
                    <div v-else-if="card.headerType === 'IMAGE'">
                        <img width="100%" :src="card.mediaUrl" :alt="''" />
                    </div>
                    <div v-else-if="card.headerType === 'DOCUMENT'">
                        <img :src="getDefaultImage()" alt="''" />
                    </div>
                </template>
                <div class="body">{{ getCardBody(card.body) }}</div>
            </div>
        </div>
    </div>
</template>

<script>
import _ from "lodash";
import { mapState } from "vuex";

export default {
    name: "WABATemplateMessage",
    props: {
        data: {
            type: Object,
            required: true,
        },
        conversationId: {
            type: String,
        },
    },
    computed: {
        ...mapState({
            siteId: (state) => _.get(state.site.site, "_id"),
            users: (state) => state.user.users.data,
        }),
    },
    created() {
        const meta = this.getTemplateParameters(this.data.payload);
        this.meta = {
            templateType: meta.templateType,
            body: meta.body,
            footer: meta.footer,
            headerText: meta.headerText,
            buttons: meta.buttons,
            cards: meta.cards,
        };
    },
    data() {
        return {
            clickedButton: null,
            meta: {
                templateType: "TEXT",
                body: "",
                footer: "",
                headerText: "",
                buttons: [],
                cards: [],
            },
        };
    },
    methods: {
        getCardBody(body) {
            // replace params
            const params = _.get(this.data, "params", []);
            return body.replace(/{{(\d+)}}/g, (match, number) => {
                return params[number - 1] || match;
            });
        },
        getDefaultImage(type) {
            const defaultImages = {
                IMAGE: "https://static.supsis.live/static/image-preview.svg",
                VIDEO: "https://static.supsis.live/static/video-preview.svg",
                DOCUMENT: "https://static.supsis.live/static/pdf-preview.svg",
            };
            return defaultImages[type];
        },
        getMetaText() {
            let { fromBroadcast, fromAPI, fromAutomation, user, userId, username } = this.data;
            if (fromBroadcast) {
                return "This message sent from Broadcast";
            }
            if (fromAPI) {
                return "This message sent from API";
            }
            if (fromAutomation) {
                return "This message sent from Automation";
            }
            if (username || user) {
                const userHashMap = _.keyBy(this.users, "_id");
                if (user) username = typeof user === "object" ? _.get(user, "name", "") : _.get(userHashMap, `[${user}].name`, "");
                if (!username) username = "User";
                return `This Template Message sent from ${username}`;
            }
            return null;
        },
        getTemplateParameters(template) {
            const metaObject = JSON.parse(template.meta || "{}");
            const containerMetaObj = JSON.parse(template.containerMeta || "{}");

            let params = [...(_.get(template, "params", []) || [])];

            const bodyFromContainerMeta = _.get(containerMetaObj, "data");
            let body = bodyFromContainerMeta || _.get(metaObject, "example") || metaObject.data;
            let headerText = _.get(containerMetaObj, "header", "") || "";
            let footer = _.get(containerMetaObj, "footer");

            if (typeof params === "string") {
                params = [params];
            }
            params = Array.isArray(params) ? params : [];

            const buttons = _.get(containerMetaObj, "buttons") || template.buttons;
            let cards = _.get(containerMetaObj, "cards", []);
            if (params.length > 0 && headerText) {
                headerText = headerText.replace(/{{\d+}}/g, (match) => {
                    const content = params[0] || match;
                    params.shift();
                    return content;
                });
            }

            if (params.length > 0 && body) {
                body = body.replace(/{{\d+}}/g, (match) => {
                    const content = params[0] || match;
                    params.shift();
                    return content;
                });
            }

            if (params.length > 0 && cards.length > 0) {
                cards = cards.map(card => {
                    card.body = card.body.replace(/{{\d+}}/g, (match) => {
                        const content = params[0] || match;
                        params.shift();
                        return content;
                    });
                    return card;
                });
            }

            return {
                templateType: template.templateType, //text|image,
                headerType: null, //text|image,
                headerMediaType: null, //image|video|document|location,
                headerMediaURL: null,
                headerText,
                footer,
                body,
                buttons: Array.isArray(buttons) && buttons.length > 0 ? buttons : [],
                cards,
            };
        },
    },
    watch: {
        data: {
            handler() {
                const meta = this.getTemplateParameters(this.data.payload);
                this.meta = {
                    templateType: meta.templateType,
                    body: meta.body,
                    footer: meta.footer,
                    headerText: meta.headerText,
                    buttons: meta.buttons,
                    cards: meta.cards,
                };
            },
            deep: true,
        },
    },
};
</script>

<style lang="scss" scoped>
.message.waba-template-message {
    display: flex;
    flex-direction: column;
    gap: 5px;
    align-items: flex-end;

    .content {
        border: 1px solid;
        border-radius: 10px;
        overflow: hidden;
        width: 240px;
        border-color: rgb(219, 225, 232);
        background: rgb(255, 255, 255);
    }

    .meta-header {
        padding: 3px;
        border-bottom: 1px solid #dbe1e8;
        font-size: 10px;
        background: #3f3f3fa1;
        color: white;
        display: flex;
        align-items: center;
        gap: 3px;

        .material-icons {
            font-size: 14px;
        }
    }

    .header-text,
    .body,
    .footer {
        background-color: #fff;
        border-radius: 4px;
        padding: 12px 8px;
    }

    .header-text {
        font-size: 16px;
        font-weight: 500;
        padding: 6px 7px 0px 9px;
        overflow-wrap: break-word;
    }

    .body {
        display: -webkit-box;
        font-size: 14px;
        font-weight: 400;
        color: rgb(63, 63, 63);
        overflow-wrap: break-word;
        word-break: break-word;
        max-width: 100%;
        overflow-y: auto;
        max-height: 400px;
        padding: 8px 8px 6px;
        white-space: pre-line;
    }

    .footer {
        font-size: 12px;
        font-weight: 500;
        line-height: 16px;
        padding: 0px 7px 8px 9px;
        color: rgb(95, 104, 128);
        overflow-wrap: break-word;
        word-break: break-word;
        display: -webkit-box;
        max-width: 100%;
        max-height: 48px;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
        margin-top: 4px;
    }

    .buttons {
        display: flex;
        flex-direction: column;
        width: 100%;

        .single-button {
            align-items: center;
            border-top: 1px solid;
            cursor: pointer;
            display: flex;
            font-weight: 600;
            min-height: 45px;
            padding: 0 10px;
            transition: all 0.3s;
            background: rgb(255, 255, 255);
            border-top-color: rgb(219, 225, 232);

            &.selected {
                background: rgb(0, 108, 255);

                .single-button-text {
                    color: white;
                }
            }

            &:hover {
                opacity: 0.8 !important;
            }

            .single-button-text {
                overflow: hidden !important;
                text-align: center;
                text-overflow: ellipsis;
                white-space: nowrap;
                width: 100%;
                color: rgb(0, 108, 255);
            }
        }
    }

    .carousel-messages {
        margin-top: 10px;
        display: flex;
        gap: 5px;
        align-items: flex-start;
        flex-wrap: nowrap;
        overflow-x: auto;

        &-item {
            background: rgb(255, 255, 255);
            border-radius: 10px;
            border: 1px solid rgb(219, 225, 232);
            display: flex;
            flex-direction: column;
            gap: 5px;
            padding: 10px;
            width: 200px;
            box-sizing: content-box;

            img {
                width: 200px;
                height: 200px;
                object-fit: cover;
                border-radius: 10px;
            }

            video {
                width: 200px;
                border-radius: 10px;
            }

            embed {
                height: 200px;
                width: 200px;
                border-radius: 10px;
            }
        }
    }
}
</style>
